import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import VerifiedUser from "@material-ui/icons/VerifiedUser";
import ReportProblemOutlined from "@material-ui/icons/ReportProblemOutlined";
import { farShared } from "../../../../assets/jss/user/far/farTokens.jsx";

const useStyles = makeStyles(theme => {
    const s = farShared(theme);
    const t = s.t;
    return {
        banner: {
            display: "flex",
            alignItems: "center",
            gap: "14px",
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid "+t.success.border,
            backgroundColor: t.success.bg,
            color: t.text,
            textAlign: "left",
            maxWidth: "100%",
            boxSizing: "border-box"
        },
        disabled: {
            borderColor: t.warning.border,
            backgroundColor: t.warning.bg
        },
        shield: {
            flexShrink: 0,
            width: "40px",
            height: "40px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: t.success.solid,
            color: t.success.onSolid,
            "& svg": { width: "24px", height: "24px" }
        },
        shieldWarning: {
            backgroundColor: t.warning.solid,
            color: t.warning.onSolid
        },
        body: { flex: 1, minWidth: 0 },
        title: {
            margin: 0,
            fontSize: "15px",
            fontWeight: 700,
            lineHeight: "20px",
            color: t.dark ? t.textStrong : t.textStrong,
            "& em": { fontStyle: "normal", color: t.dark ? t.success.fg : t.success.fg }
        },
        text: {
            margin: "2px 0 0",
            fontSize: "13px",
            lineHeight: "18px",
            color: t.textMuted,
            "& a": { color: t.link, textDecoration: "underline", fontWeight: 600 }
        },
        action: {
            ...s.secondaryButton,
            flexShrink: 0,
            textDecoration: "none !important",
            whiteSpace: "nowrap"
        },
        [theme.breakpoints.down("xs")]: {
            banner: { flexWrap: "wrap" },
            action: { width: "100%" }
        }
    };
});

function FarStatusBanner({ enabled, variant, onViewRules, className, canManage, description }){
    const classes = useStyles();
    if(variant === "send"){
        return (
            <div className={classes.banner+" "+className} role="status">
                <span className={classes.shield} aria-hidden="true"><VerifiedUser /></span>
                <div className={classes.body}>
                    <p className={classes.title}>File Attachment Rules (FAR) <em>is active</em></p>
                    <p className={classes.text}>{description || "Your files and recipients will be checked before sending."}</p>
                </div>
                {
                    onViewRules ?
                        <button type="button" className={classes.action} onClick={onViewRules}>View Rules</button>
                    : null
                }
            </div>
        );
    }
    if(!enabled){
        return (
            <div className={classes.banner+" "+classes.disabled+" "+className} role="status">
                <span className={classes.shield+" "+classes.shieldWarning} aria-hidden="true"><ReportProblemOutlined /></span>
                <div className={classes.body}>
                    <p className={classes.title}>File Attachment Rules are disabled</p>
                    <p className={classes.text}>
                        Rules are not checked when sending.{" "}
                        {canManage ? <RouterLink to="/user/extensions">Enable in Extensions</RouterLink> : "Ask your administrator to enable it in Extensions."}
                    </p>
                </div>
            </div>
        );
    }
    return (
        <div className={classes.banner+" "+className} role="status">
            <span className={classes.shield} aria-hidden="true"><VerifiedUser /></span>
            <div className={classes.body}>
                <p className={classes.title}>File Attachment Rules are enabled</p>
                <p className={classes.text}>
                    Managed from <RouterLink to="/user/extensions">Extensions</RouterLink>.
                </p>
            </div>
        </div>
    );
}

FarStatusBanner.defaultProps = {
    enabled: true,
    variant: "page",
    onViewRules: null,
    className: "",
    canManage: true
};

FarStatusBanner.propTypes = {
    enabled: PropTypes.bool,
    variant: PropTypes.oneOf(["page", "send"]),
    /** Send variant: replaces "Your files and recipients will be checked before sending." */
    description: PropTypes.string,
    onViewRules: PropTypes.func,
    className: PropTypes.string,
    canManage: PropTypes.bool
};

export default FarStatusBanner;
